import React, { useState } from 'react';
import {
  X, Plus, Trash2, Pencil, ArrowUp, ArrowDown, Filter, ChevronRight, Sigma, Link2, Bold, Italic, AlignLeft, AlignCenter, AlignRight, Sparkles, Loader2,
} from 'lucide-react';
import {
  WIDGET_TYPES, DATE_PRESETS, OPS, CALC_FORMATS, isDateDim, parseRef, makeCtx, commonDims, typeDefaults, newPart, newId, sourceLogo,
} from './lib';
import { FieldPicker, Logo, FmtBadge } from './Fields';
import { TypeIcon } from './TypeIcon';
import { TEXT_COLORS, textProps } from './Widgets';

const label = 'block text-xs font-medium text-gray-600 mb-1';
const input = 'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500';
const MULTI_TYPES = ['table', 'line', 'area', 'bar', 'hbar'];

function Field({ title, children, hint }) {
  return (
    <div>
      {title && <span className={label}>{title}</span>}
      {children}
      {hint && <p className="mt-1 text-[11px] text-gray-500 leading-snug">{hint}</p>}
    </div>
  );
}

function Section({ title, children, action }) {
  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

function Segmented({ options, value, onChange }) {
  return (
    <div className="inline-flex rounded-lg border border-gray-300 bg-white p-0.5">
      {options.map((o) => (
        <button key={o.key} type="button" title={o.title || ''} onClick={() => onChange(o.key)}
          className={`px-2.5 py-1 text-xs rounded-md inline-flex items-center gap-1 ${value === o.key ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ─── Seçici grupları ─────────────────────────────────────────────────────────
function sourceGroups(sources) {
  const groups = {};
  sources.forEach((s) => {
    (groups[s.group] = groups[s.group] || []).push({
      value: s.key, label: s.label, logo: sourceLogo(s.key), disabled: !s.connected, hint: s.connected ? '' : 'not connected',
    });
  });
  return Object.entries(groups).map(([g, items]) => ({ key: g, label: g, items }));
}

function metricGroups(w, sourcesByKey, { fmtLock, additiveOnly, withCalcs = true, ctx } = {}) {
  const groups = (w.parts || []).map((p) => {
    const src = sourcesByKey[p.source];
    return {
      key: p.id,
      label: p.alias || src?.label || 'Source',
      logo: sourceLogo(p.source),
      sourceKey: p.source,
      items: (src?.metrics || []).map((m) => {
        const off = (fmtLock && m.fmt !== fmtLock) || (additiveOnly && m.derived);
        return {
          value: `${p.id}:${m.key}`, label: m.label, fmt: m.fmt, disabled: off,
          hint: off ? (additiveOnly ? 'ratio' : 'different unit') : '',
        };
      }),
    };
  });
  if (withCalcs && (w.calcs || []).length) {
    groups.push({
      key: 'calc', label: 'Calculated', logo: 'calc',
      items: w.calcs.map((c) => {
        const meta = ctx.metric(`calc:${c.id}`);
        const off = (fmtLock && meta.fmt !== fmtLock) || additiveOnly;
        return { value: `calc:${c.id}`, label: meta.label, fmt: meta.fmt, disabled: off, hint: off ? 'not available' : '' };
      }),
    });
  }
  return groups;
}

function dimGroups(dims, { only, multi } = {}) {
  const list = dims.filter((d) => (only === 'date' ? d.isDate : only === 'nodate' ? !d.isDate : true));
  return [
    { key: 'date', label: 'Date', items: list.filter((d) => d.isDate).map((d) => ({ value: d.key, label: d.label })) },
    { key: 'dims', label: multi ? 'Shared dimensions' : 'Dimensions', items: list.filter((d) => !d.isDate).map((d) => ({ value: d.key, label: d.label })) },
  ].filter((g) => g.items.length);
}

// ─── Parça (veri kaynağı) kartı ──────────────────────────────────────────────
function Filters({ dims, filters, onChange }) {
  const set = (i, patch) => onChange(filters.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  return (
    <div className="space-y-2">
      {filters.map((f, i) => (
        <div key={i} className="rounded-lg bg-gray-50 p-2 space-y-1.5">
          <div className="flex gap-1.5">
            <select className={`${input} py-1.5`} value={f.dimension} onChange={(e) => set(i, { dimension: e.target.value })}>
              {dims.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
            </select>
            <button type="button" className="p-1.5 text-gray-400 hover:text-rose-600" onClick={() => onChange(filters.filter((_, j) => j !== i))} aria-label="Remove filter"><Trash2 className="w-4 h-4" /></button>
          </div>
          <div className="flex gap-1.5">
            <select className={`${input} py-1.5 w-[44%]`} value={f.op} onChange={(e) => set(i, { op: e.target.value })}>
              <option value="contains">contains</option>
              <option value="not_contains">does not contain</option>
              <option value="equals">equals</option>
              <option value="not_equals">does not equal</option>
              <option value="in">is one of</option>
              <option value="not_in">is none of</option>
            </select>
            <input className={`${input} py-1.5`} value={f.value} placeholder={f.op === 'in' || f.op === 'not_in' ? 'value 1 | value 2' : 'value'} onChange={(e) => set(i, { value: e.target.value })} />
          </div>
        </div>
      ))}
      {dims.length > 0 ? (
        <button type="button" className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:text-blue-900"
          onClick={() => onChange([...filters, { dimension: dims.find((d) => d.key === 'platform')?.key || dims[0].key, op: 'equals', value: '' }])}>
          <Plus className="w-3.5 h-3.5" /> Add filter
        </button>
      ) : <p className="text-[11px] text-gray-500">This source has no dimensions to filter on.</p>}
    </div>
  );
}

function PartCard({ part, index, sources, sourcesByKey, canRemove, onChangeSource, onChange, onRemove }) {
  const [showFilters, setShowFilters] = useState((part.filters || []).length > 0);
  const src = sourcesByKey[part.source];
  const dims = (src?.dimensions || []).filter((d) => !d.isDate);
  const n = (part.filters || []).filter((f) => f.value).length;
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3 space-y-2">
      <div className="flex items-center gap-2">
        <span className="w-5 h-5 rounded-full bg-gray-100 text-[11px] font-semibold text-gray-500 flex items-center justify-center shrink-0">{index + 1}</span>
        <div className="flex-1 min-w-0">
          <FieldPicker groups={sourceGroups(sources)} value={part.source} onChange={onChangeSource} placeholder="Select a source" />
        </div>
        {canRemove && (
          <button type="button" onClick={onRemove} className="p-1.5 text-gray-400 hover:text-rose-600" aria-label="Remove source"><Trash2 className="w-4 h-4" /></button>
        )}
      </div>
      <div className="flex items-center gap-2 pl-7">
        <input className={`${input} py-1.5 text-xs`} value={part.alias || ''} placeholder={`Label (e.g. ${src?.label || 'source'})`} onChange={(e) => onChange({ alias: e.target.value })} />
        <button type="button" onClick={() => setShowFilters((v) => !v)}
          className={`shrink-0 inline-flex items-center gap-1 rounded-lg border px-2 py-1.5 text-xs ${n ? 'border-blue-300 bg-blue-50 text-blue-800' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
          <Filter className="w-3.5 h-3.5" />Filter{n ? ` (${n})` : ''}
          <ChevronRight className={`w-3.5 h-3.5 transition-transform ${showFilters ? 'rotate-90' : ''}`} />
        </button>
      </div>
      {showFilters && (
        <div className="pl-7"><Filters dims={dims} filters={part.filters || []} onChange={(v) => onChange({ filters: v })} /></div>
      )}
    </div>
  );
}

// ─── Metrik seçimi ───────────────────────────────────────────────────────────
function MetricChips({ w, ctx, sourcesByKey, onChange, max = 10, fmtLock }) {
  const selected = (w.metrics || []).filter(Boolean);
  const move = (i, d) => {
    const next = [...selected];
    const j = i + d;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const lock = fmtLock && selected.length ? ctx.metric(selected[0]).fmt : null;
  return (
    <div className="space-y-1.5">
      {selected.map((ref, i) => {
        const m = ctx.metric(ref);
        return (
          <div key={ref} className={`group flex items-center gap-2 rounded-lg border px-2.5 py-1.5 ${m.missing ? 'border-rose-200 bg-rose-50' : 'border-gray-200 bg-white'}`}>
            <Logo name={m.isCalc ? 'calc' : m.logo} sourceKey={m.sourceKey} className="w-3.5 h-3.5" />
            <span className="flex-1 min-w-0 truncate text-sm text-gray-800" title={m.fullLabel}>
              {m.label}{ctx.multi && !m.isCalc && <span className="text-gray-400"> · {m.partLabel}</span>}
            </span>
            <FmtBadge fmt={m.fmt} />
            <span className="flex items-center opacity-0 group-hover:opacity-100">
              <button type="button" onClick={() => move(i, -1)} className="p-0.5 text-gray-400 hover:text-gray-800" aria-label="Move up"><ArrowUp className="w-3.5 h-3.5" /></button>
              <button type="button" onClick={() => move(i, 1)} className="p-0.5 text-gray-400 hover:text-gray-800" aria-label="Move down"><ArrowDown className="w-3.5 h-3.5" /></button>
            </span>
            {selected.length > 1 && (
              <button type="button" onClick={() => onChange(selected.filter((x) => x !== ref))} className="p-0.5 text-gray-400 hover:text-rose-600" aria-label="Remove"><X className="w-3.5 h-3.5" /></button>
            )}
          </div>
        );
      })}
      {selected.length < max && (
        <FieldPicker
          multiple
          groups={metricGroups(w, sourcesByKey, { fmtLock: lock, ctx })}
          value={selected}
          onChange={(v) => onChange(v.slice(0, max))}
          renderButton={({ toggle }) => (
            <button type="button" onClick={toggle} className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-gray-300 py-2 text-sm text-gray-600 hover:border-blue-400 hover:text-blue-700">
              <Plus className="w-4 h-4" />Add metric
            </button>
          )}
        />
      )}
    </div>
  );
}

// ─── Hesaplanmış metrikler ───────────────────────────────────────────────────
function CalcForm({ w, ctx, sourcesByKey, initial, onSave, onCancel }) {
  const [c, setC] = useState(initial || { label: '', a: '', op: '/', b: '', fmt: 'currency' });
  const groups = metricGroups(w, sourcesByKey, { withCalcs: false, ctx });
  const ok = c.a && c.b;
  return (
    <div className="rounded-xl border border-violet-200 bg-violet-50/40 p-3 space-y-2">
      <input className={`${input} py-1.5`} value={c.label} placeholder="Name (e.g. Cost per session)" onChange={(e) => setC({ ...c, label: e.target.value })} />
      <FieldPicker groups={groups} value={c.a} onChange={(v) => setC({ ...c, a: v })} placeholder="First metric" />
      <Segmented options={OPS} value={c.op} onChange={(v) => setC({ ...c, op: v })} />
      <FieldPicker groups={groups} value={c.b} onChange={(v) => setC({ ...c, b: v })} placeholder="Second metric" />
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-600">Format</span>
        <select className={`${input} py-1.5`} value={c.fmt} onChange={(e) => setC({ ...c, fmt: e.target.value })}>
          {CALC_FORMATS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
        </select>
      </div>
      {c.a && c.b && <p className="text-[11px] text-gray-600">= {ctx.metric(c.a).fullLabel} {OPS.find((o) => o.key === c.op)?.label} {ctx.metric(c.b).fullLabel}</p>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-900">Cancel</button>
        <button type="button" disabled={!ok} onClick={() => onSave(c)} className="px-3 py-1.5 text-xs font-medium rounded-lg bg-violet-600 text-white disabled:opacity-40 hover:bg-violet-700">Save</button>
      </div>
    </div>
  );
}

function Calcs({ w, ctx, sourcesByKey, onChange }) {
  const [editing, setEditing] = useState(null); // 'new' | calc id
  const calcs = w.calcs || [];
  const single = !MULTI_TYPES.includes(w.type) || (w.breakdown && w.type !== 'table');
  const save = (c) => {
    if (editing === 'new') {
      const id = newId('c');
      const ref = `calc:${id}`;
      onChange({ calcs: [...calcs, { ...c, id }], metrics: single ? [ref] : [...(w.metrics || []), ref] });
    } else {
      onChange({ calcs: calcs.map((x) => (x.id === editing ? { ...c, id: editing } : x)) });
    }
    setEditing(null);
  };
  const remove = (id) => {
    const metrics = (w.metrics || []).filter((r) => r !== `calc:${id}`);
    onChange({ calcs: calcs.filter((x) => x.id !== id), metrics });
  };
  return (
    <div className="space-y-1.5">
      {calcs.map((c) => (editing === c.id ? (
        <CalcForm key={c.id} w={w} ctx={ctx} sourcesByKey={sourcesByKey} initial={c} onSave={save} onCancel={() => setEditing(null)} />
      ) : (
        <div key={c.id} className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5">
          <Sigma className="w-3.5 h-3.5 text-violet-600 shrink-0" />
          <span className="flex-1 min-w-0 truncate text-sm text-gray-800" title={ctx.metric(`calc:${c.id}`).label}>{ctx.metric(`calc:${c.id}`).label}</span>
          <FmtBadge fmt={c.fmt} />
          <button type="button" onClick={() => setEditing(c.id)} className="p-0.5 text-gray-400 hover:text-gray-800" aria-label="Edit"><Pencil className="w-3.5 h-3.5" /></button>
          <button type="button" onClick={() => remove(c.id)} className="p-0.5 text-gray-400 hover:text-rose-600" aria-label="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      )))}
      {editing === 'new'
        ? <CalcForm w={w} ctx={ctx} sourcesByKey={sourcesByKey} onSave={save} onCancel={() => setEditing(null)} />
        : (
          <button type="button" onClick={() => setEditing('new')} className="inline-flex items-center gap-1 text-xs font-medium text-violet-700 hover:text-violet-900">
            <Plus className="w-3.5 h-3.5" /> Add calculated metric
          </button>
        )}
    </div>
  );
}

// ─── Metin ayarları ──────────────────────────────────────────────────────────
function TextOptions({ w, set }) {
  const t = textProps(w);
  return (
    <>
      <Field title="Text">
        <textarea className={`${input} min-h-[120px]`} value={w.text || ''} onChange={(e) => set({ text: e.target.value })} />
      </Field>
      <Field title="Font size">
        <Segmented value={t.size} onChange={(v) => set({ textSize: v })}
          options={[{ key: 'sm', label: 'S' }, { key: 'base', label: 'M' }, { key: 'lg', label: 'L' }, { key: 'xl', label: 'XL' }, { key: '2xl', label: '2XL' }, { key: '3xl', label: '3XL' }, { key: '4xl', label: '4XL' }]} />
      </Field>
      <div className="flex flex-wrap gap-4">
        <Field title="Style">
          <div className="inline-flex gap-1">
            <button type="button" onClick={() => set({ textBold: !t.bold })} className={`p-1.5 rounded-md border ${t.bold ? 'bg-gray-900 text-white border-gray-900' : 'border-gray-300 text-gray-600 hover:bg-gray-100'}`} aria-label="Bold"><Bold className="w-4 h-4" /></button>
            <button type="button" onClick={() => set({ textItalic: !t.italic })} className={`p-1.5 rounded-md border ${t.italic ? 'bg-gray-900 text-white border-gray-900' : 'border-gray-300 text-gray-600 hover:bg-gray-100'}`} aria-label="Italic"><Italic className="w-4 h-4" /></button>
          </div>
        </Field>
        <Field title="Horizontal alignment">
          <Segmented value={t.align} onChange={(v) => set({ textAlign: v })}
            options={[{ key: 'left', label: <AlignLeft className="w-4 h-4" />, title: 'Left' }, { key: 'center', label: <AlignCenter className="w-4 h-4" />, title: 'Center' }, { key: 'right', label: <AlignRight className="w-4 h-4" />, title: 'Right' }]} />
        </Field>
      </div>
      <Field title="Vertical alignment">
        <Segmented value={t.vAlign} onChange={(v) => set({ textVAlign: v })}
          options={[{ key: 'top', label: 'Top' }, { key: 'middle', label: 'Middle' }, { key: 'bottom', label: 'Bottom' }]} />
      </Field>
      <Field title="Text color">
        <div className="flex gap-1.5">
          {Object.entries(TEXT_COLORS).map(([k, hex]) => (
            <button key={k} type="button" onClick={() => set({ textColor: k })} aria-label={k}
              className={`w-7 h-7 rounded-full border ${t.color === k ? 'ring-2 ring-blue-500 ring-offset-1' : ''}`} style={{ background: hex, borderColor: '#d1d5db' }} />
          ))}
        </div>
      </Field>
      <Field title="Background">
        <Segmented value={w.textBg || 'white'} onChange={(v) => set({ textBg: v, ...(v === 'dark' && t.color === 'dark' ? { textColor: 'white' } : {}) })}
          options={[{ key: 'white', label: 'Card' }, { key: 'none', label: 'Transparent' }, { key: 'blue', label: 'Blue' }, { key: 'dark', label: 'Dark' }]} />
      </Field>
    </>
  );
}

// ─── Editör ──────────────────────────────────────────────────────────────────
// AI ile düzenleme: "haftalık yap", "Meta'yı filtrele" gibi tarifler bu widget'ı değiştirir.
function AiEdit({ busy, onSubmit }) {
  const [text, setText] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    const v = text.trim();
    if (!v || busy) return;
    if (await onSubmit(v)) setText('');
  };
  return (
    <form onSubmit={submit} className="flex items-center gap-1.5 rounded-lg border border-violet-200 bg-white px-2.5 py-1.5 focus-within:border-violet-400 focus-within:ring-2 focus-within:ring-violet-500/20">
      <Sparkles className="w-4 h-4 text-violet-600 shrink-0" />
      <input value={text} disabled={busy} onChange={(e) => setText(e.target.value)} placeholder='Describe a change, e.g. "make it weekly, only Meta"'
        className="flex-1 min-w-0 bg-transparent text-sm outline-none placeholder:text-gray-400 disabled:opacity-60" />
      <button type="submit" disabled={busy || !text.trim()} className="shrink-0 rounded-md bg-violet-600 px-2 py-1 text-xs font-medium text-white hover:bg-violet-700 disabled:opacity-40">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Apply'}
      </button>
    </form>
  );
}

export default function WidgetEditor({ widget: w, sources, onChange, onClose, aiBusy, onAiEdit }) {
  const sourcesByKey = Object.fromEntries(sources.map((s) => [s.key, s]));
  const ctx = makeCtx(w, sourcesByKey);
  const set = (patch) => onChange(patch);
  const parts = w.parts || [];
  const dims = commonDims(w, sourcesByKey);
  const multiMetric = MULTI_TYPES.includes(w.type) && !(w.breakdown && w.type !== 'table');
  const chart = ['line', 'area', 'bar', 'hbar', 'stacked'].includes(w.type);

  // Boyut alanlarını yeni ortak boyut listesine göre onarır.
  const fixDims = (next) => {
    const d = commonDims(next, sourcesByKey);
    const has = (k) => !k || d.some((x) => x.key === k);
    const def = typeDefaults(next.type, d, next.metrics || []);
    const patch = {};
    ['dimension', 'dimension2', 'breakdown', 'pivotRow', 'pivotCol'].forEach((f) => {
      if (next[f] && !has(next[f])) patch[f] = def[f] ?? '';
    });
    return patch;
  };

  const changeType = (type) => {
    if (type === 'text') return set({ type, text: w.text || 'Heading' });
    let next = { ...w, type };
    if (!parts.length) {
      const s = sources.find((x) => x.key === 'ads_all' && x.connected) || sources.find((x) => x.connected);
      next = { ...next, parts: [newPart(s?.key)], calcs: [] };
      const src = sourcesByKey[s?.key];
      const k = ['cost', 'revenue', 'sessions'].find((x) => src?.metrics?.some((m) => m.key === x)) || src?.metrics?.[0]?.key;
      next.metrics = k ? [`${next.parts[0].id}:${k}`] : [];
    }
    const defs = typeDefaults(type, commonDims(next, sourcesByKey), next.metrics || []);
    return set({ ...defs, type, parts: next.parts, calcs: next.calcs || [], metrics: defs.metrics });
  };

  const updatePart = (id, patch) => {
    const nextParts = parts.map((p) => (p.id === id ? { ...p, ...patch } : p));
    set({ parts: nextParts });
  };

  const changePartSource = (id, sourceKey) => {
    const src = sourcesByKey[sourceKey];
    const keys = new Set((src?.metrics || []).map((m) => m.key));
    const nextParts = parts.map((p) => (p.id === id ? { ...p, source: sourceKey, filters: [] } : p));
    // Aynı adlı metrik yeni kaynakta da varsa korunur.
    let metrics = (w.metrics || []).filter((r) => { const x = parseRef(r); return x.partId !== id || keys.has(x.metric); });
    const calcs = (w.calcs || []).filter((c) => [c.a, c.b].every((r) => { const x = parseRef(r); return x.partId !== id || keys.has(x.metric); }));
    metrics = metrics.filter((r) => { const x = parseRef(r); return !x.calc || calcs.some((c) => c.id === x.calc); });
    if (!metrics.some((r) => parseRef(r).partId === id) && (multiMetric || !metrics.length)) {
      const k = ['cost', 'sessions', 'revenue', 'clicks', 'orders'].find((x) => keys.has(x)) || [...keys][0];
      if (k) metrics = multiMetric ? [...metrics, `${id}:${k}`] : [`${id}:${k}`];
    }
    const next = { ...w, parts: nextParts, metrics, calcs };
    set({ parts: nextParts, metrics, calcs, ...fixDims(next) });
  };

  const addPart = () => {
    const used = new Set(parts.map((p) => p.source));
    const firstIsAd = sourcesByKey[parts[0]?.source]?.group === 'Advertising';
    const pick = (firstIsAd && sources.find((s) => s.key === 'ga4_traffic' && s.connected))
      || sources.find((s) => s.connected && !used.has(s.key)) || sources.find((s) => s.connected);
    const part = newPart(pick?.key);
    const src = sourcesByKey[pick?.key];
    const k = ['sessions', 'revenue', 'cost', 'clicks'].find((x) => src?.metrics?.some((m) => m.key === x)) || src?.metrics?.[0]?.key;
    const metrics = multiMetric && k ? [...(w.metrics || []), `${part.id}:${k}`] : (w.metrics || []);
    const next = { ...w, parts: [...parts, part], metrics };
    // Ortak boyut yoksa platform en iyi birleştirme anahtarı.
    const patch = fixDims(next);
    set({ parts: next.parts, metrics, ...patch });
  };

  const removePart = (id) => {
    const nextParts = parts.filter((p) => p.id !== id);
    const calcs = (w.calcs || []).filter((c) => parseRef(c.a).partId !== id && parseRef(c.b).partId !== id);
    let metrics = (w.metrics || []).filter((r) => {
      const x = parseRef(r);
      return x.calc ? calcs.some((c) => c.id === x.calc) : x.partId !== id;
    });
    if (!metrics.length && nextParts[0]) {
      const src = sourcesByKey[nextParts[0].source];
      const k = src?.metrics?.[0]?.key;
      if (k) metrics = [`${nextParts[0].id}:${k}`];
    }
    set({ parts: nextParts, calcs, metrics });
  };

  // Bileşen değil düz fonksiyon: her çizimde yeniden kurulup açık seçiciyi kapatmasın.
  const dimPick = ({ value, onPick, only, emptyLabel }) => (
    <FieldPicker groups={dimGroups(dims, { only, multi: ctx.multi })} value={value || ''} onChange={onPick} emptyLabel={emptyLabel} />
  );
  const singleMetric = ({ additiveOnly }) => (
    <FieldPicker groups={metricGroups(w, sourcesByKey, { additiveOnly, ctx })} value={w.metrics?.[0] || ''} onChange={(v) => set({ metrics: [v] })} placeholder="Select a metric" />
  );

  const hasAd = parts.some((p) => sourcesByKey[p.source]?.group === 'Advertising');
  const hasGa = parts.some((p) => p.source === 'ga4_traffic');
  const joinDims = [w.dimension, w.dimension2, w.breakdown, w.pivotRow, w.pivotCol].filter(Boolean);

  return (
    <aside className="fixed right-0 top-0 bottom-0 z-50 w-full sm:w-[380px] bg-white border-l border-gray-200 shadow-2xl flex flex-col">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <h2 className="text-sm font-semibold text-gray-900">Edit widget</h2>
        <button type="button" onClick={onClose} className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100" aria-label="Close"><X className="w-4 h-4" /></button>
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6 bg-gray-50/50">
        {onAiEdit && <AiEdit busy={aiBusy} onSubmit={onAiEdit} />}
        <Section title="Display">
          {w.type !== 'text' && (
            <input className={input} value={w.title || ''} placeholder="Title (automatic if left empty)" onChange={(e) => set({ title: e.target.value })} />
          )}
          <div className="grid grid-cols-5 gap-1.5">
            {WIDGET_TYPES.map((t) => (
              <button key={t.key} type="button" title={t.label} onClick={() => changeType(t.key)}
                className={`flex flex-col items-center justify-center gap-1 rounded-lg border px-1 py-2 text-[10px] leading-tight bg-white ${w.type === t.key ? 'border-blue-500 bg-blue-50 text-blue-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                <TypeIcon type={t.key} className="w-4 h-4" />
                <span className="truncate w-full text-center">{t.label.split(' ')[0]}</span>
              </button>
            ))}
          </div>
        </Section>

        {w.type === 'text' ? (
          <Section title="Text"><TextOptions w={w} set={set} /></Section>
        ) : (
          <>
            <Section title="Data sources"
              action={parts.length < 5 && (
                <button type="button" onClick={addPart} className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:text-blue-900">
                  <Link2 className="w-3.5 h-3.5" />Add source
                </button>
              )}>
              {parts.map((p, i) => (
                <PartCard key={p.id} part={p} index={i} sources={sources} sourcesByKey={sourcesByKey} canRemove={parts.length > 1}
                  onChangeSource={(v) => changePartSource(p.id, v)} onChange={(patch) => updatePart(p.id, patch)} onRemove={() => removePart(p.id)} />
              ))}
              {parts.length > 1 && (
                <p className="text-[11px] text-gray-500 leading-snug">
                  Sources are joined on the dimension you pick: rows with the same {joinDims.length ? joinDims.map((k) => ctx.dim(k).label).join(' / ') : 'dimension'} value are combined.
                  Shared dimensions: {dims.filter((d) => !d.isDate).map((d) => d.label).join(', ') || 'date only'}.
                  {hasAd && hasGa && ' On the GA4 side, "Platform / Channel" is derived from source/medium (e.g. facebook/ig + cpc/paid → Meta Ads).'}
                </p>
              )}
            </Section>

            <Section title={multiMetric ? 'Metrics' : 'Metric'}>
              {multiMetric
                ? <MetricChips w={w} ctx={ctx} sourcesByKey={sourcesByKey} onChange={(v) => set({ metrics: v })} max={w.type === 'table' ? 12 : 4} fmtLock={chart} />
                : singleMetric({ additiveOnly: w.type === 'pie' })}
              {chart && multiMetric && <p className="text-[11px] text-gray-500">Single-axis rule: only metrics with the same unit can be plotted together.</p>}
              <div className="pt-1">
                <span className={label}>Calculated metrics</span>
                <Calcs w={w} ctx={ctx} sourcesByKey={sourcesByKey} onChange={set} />
              </div>
            </Section>

            <Section title="Dimensions">
              {w.type === 'kpi' && (
                <Field title="Compare">
                  <Segmented value={w.compare || 'none'} onChange={(v) => set({ compare: v })}
                    options={[{ key: 'none', label: 'None' }, { key: 'previous', label: 'Previous period' }, { key: 'yoy', label: 'Last year' }]} />
                </Field>
              )}

              {chart && (
                <>
                  <Field title={w.type === 'hbar' ? 'Bars' : 'X axis'}>
                    {dimPick({ value: w.dimension, onPick: (v) => set({ dimension: v }), only: ['line', 'area'].includes(w.type) ? 'date' : undefined })}
                  </Field>
                  <Field title="Breakdown" hint={w.breakdown ? 'Shows the top 7 values; the rest are grouped into "Other". Only one metric is plotted with a breakdown.' : 'E.g. separate series per platform or campaign.'}>
                    {dimPick({
                      value: w.breakdown, only: 'nodate', emptyLabel: w.type === 'stacked' ? undefined : 'No breakdown',
                      onPick: (v) => set({ breakdown: v, metrics: v ? (w.metrics || []).slice(0, 1) : w.metrics }),
                    })}
                  </Field>
                  {['bar', 'hbar', 'stacked'].includes(w.type) && !isDateDim(w.dimension) && (
                    <Field title="Number of values to show">
                      <input type="number" min={1} max={50} className={input} value={w.limit || 10} onChange={(e) => set({ limit: Math.max(1, Math.min(50, Number(e.target.value) || 10)) })} />
                    </Field>
                  )}
                </>
              )}

              {w.type === 'pie' && (
                <>
                  <Field title="Slices">{dimPick({ value: w.dimension, onPick: (v) => set({ dimension: v }) })}</Field>
                  <Field title="Number of slices" hint="The rest are grouped into an 'Other' slice.">
                    <input type="number" min={2} max={7} className={input} value={w.limit || 6} onChange={(e) => set({ limit: Math.max(2, Math.min(7, Number(e.target.value) || 6)) })} />
                  </Field>
                </>
              )}

              {w.type === 'table' && (
                <>
                  <Field title="Row dimension">{dimPick({ value: w.dimension, onPick: (v) => set({ dimension: v, sort: undefined }) })}</Field>
                  <Field title="Second dimension">{dimPick({ value: w.dimension2, emptyLabel: 'None', onPick: (v) => set({ dimension2: v, sort: undefined }) })}</Field>
                  <Field title="Number of rows">
                    <input type="number" min={1} max={500} className={input} value={w.limit || 25} onChange={(e) => set({ limit: Math.max(1, Math.min(500, Number(e.target.value) || 25)) })} />
                  </Field>
                </>
              )}

              {w.type === 'pivot' && (
                <>
                  <Field title="Rows">{dimPick({ value: w.pivotRow, onPick: (v) => set({ pivotRow: v }) })}</Field>
                  <Field title="Columns">{dimPick({ value: w.pivotCol, onPick: (v) => set({ pivotCol: v }) })}</Field>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input type="checkbox" checked={!!w.heatmap} onChange={(e) => set({ heatmap: e.target.checked })} className="rounded border-gray-300" />
                    Color cells by value
                  </label>
                  {w.pivotRow === w.pivotCol && <p className="text-[11px] text-rose-600">Rows and columns must use different dimensions.</p>}
                </>
              )}

              {w.type === 'kpi' && <p className="text-[11px] text-gray-500">The KPI card shows the total for the selected range.</p>}
            </Section>

            <Section title="Date range">
              <select className={input} value={w.dateMode === 'custom' ? (w.dateRange?.preset || 'last30') : 'report'}
                onChange={(e) => (e.target.value === 'report'
                  ? set({ dateMode: 'report' })
                  : set({ dateMode: 'custom', dateRange: { ...(w.dateRange || {}), preset: e.target.value } }))}>
                <option value="report">Use report date range</option>
                {DATE_PRESETS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
              {w.dateMode === 'custom' && w.dateRange?.preset === 'custom' && (
                <div className="flex gap-1.5">
                  <input type="date" className={`${input} py-1.5`} value={w.dateRange.start || ''} onChange={(e) => set({ dateRange: { ...w.dateRange, start: e.target.value } })} />
                  <input type="date" className={`${input} py-1.5`} value={w.dateRange.end || ''} onChange={(e) => set({ dateRange: { ...w.dateRange, end: e.target.value } })} />
                </div>
              )}
            </Section>
          </>
        )}
      </div>
      <div className="px-5 py-3 border-t border-gray-100">
        <button type="button" onClick={onClose} className="w-full rounded-lg bg-gray-900 text-white text-sm font-medium py-2 hover:bg-gray-800">Done</button>
      </div>
    </aside>
  );
}
